import { PostHog } from 'posthog-react-native';
import { logError } from '@/utils/logHandling';
import constants, { isPatientApp } from '@/config/constants';
import { Platform } from 'react-native';
import { User } from '@/stores/store.types';

export enum EventName {
  START_PRACTICE_CLICKED = 'start_practice_clicked',
  EXERCISE_VIEWED = 'exercise_viewed',
  SCREEN_VIEWED = 'screen_viewed',
}

interface CaptureEvent {
  userId: string;
  event: string;
  props?: Record<string, unknown>;
  overrideTimestamp?: Date | undefined;
}

export interface EventServiceProvider {
  capture(event: CaptureEvent): Promise<void>;
  shutdown(): Promise<void>;
}

export interface EventsService {
  capture(
    user: User | null,
    event: EventName,
    props?: Record<string, unknown>,
    overrideTimestamp?: Date,
  ): Promise<void>;
  shutdown(): Promise<void>;
}

export function createPostHogProvider(posthog: PostHog): EventServiceProvider {
  return {
    async capture({
      userId,
      event,
      props = {},
      overrideTimestamp = undefined,
    }) {
      posthog.capture(
        event,
        {
          ...props,
          userId,
        },
        {
          timestamp: overrideTimestamp,
          disableGeoip: true,
        },
      );
    },
    async shutdown() {
      await posthog.shutdown();
    },
  };
}

export function createEventService(
  provider: EventServiceProvider,
): EventsService {
  return {
    async capture(
      user: User | null,
      event: EventName,
      props?: Record<string | number, any>,
      overrideTimestamp?: Date,
    ) {
      try {
        const propertiesToCapture = {
          ...getRequestProperties(),
          ...props,
        };
        await provider.capture({
          userId: user?._id || 'anonymous',
          event,
          props: propertiesToCapture,
          overrideTimestamp,
        });
      } catch (error) {
        logError('Error capturing event.', error);
      }
    },
    async shutdown() {
      await provider.shutdown();
    },
  };
}

function getRequestProperties() {
  return {
    app_version: constants.appVersion,
    platform: Platform.OS,
    platform_version: Platform.Version,
    app_id: isPatientApp
      ? 'neolexon-aphasia-patient'
      : 'neolexon-aphasia-therapist',
  };
}
